import { DATE_FORMAT_STYLES, DURATION_FORMAT_STYLES } from '../../binding/formatAllowlists';
export const ALLOWED_SPACING_TOKENS = ['100', '200', '300', '400'];
export const ALLOWED_STACK_ALIGNS = ['start', 'center', 'end', 'stretch'];
export const ALLOWED_JUSTIFIES = ['start', 'center', 'end', 'between'];
export const ALLOWED_STACK_OVERFLOWS = ['auto', 'hidden'];
export const ALLOWED_ROW_ALIGNS = ['start', 'center', 'end'];
export const ALLOWED_SIZES = ['sm', 'md', 'lg', 'xl'];
export const ALLOWED_WEIGHTS = ['normal', 'bold'];
export const ALLOWED_TEXT_ALIGNS = ['start', 'center', 'end'];
export const ALLOWED_COLORS = ['default', 'secondary', 'accent', 'muted', 'danger'];
export const ALLOWED_BUTTON_VARIANTS = ['primary', 'secondary', 'danger', 'link'];
export const ALLOWED_INPUT_TYPES = ['text', 'email', 'tel', 'number'];
export const ALLOWED_LABEL_LAYOUTS = ['block', 'inline'];
export const ALLOWED_SELECT_WIDTHS = ['full', 'auto'];
export const ALLOWED_DATE_FORMAT_STYLES = DATE_FORMAT_STYLES;
export const ALLOWED_DURATION_FORMAT_STYLES = DURATION_FORMAT_STYLES;
export function isString(value) {
  return typeof value === 'string';
}
export function inList(list, value) {
  return list.indexOf(value) !== -1;
}
export function sanitizeString(value) {
  return isString(value) ? value : undefined;
}
export function sanitizeBoolean(value) {
  return typeof value === 'boolean' ? value : undefined;
}
export function sanitizePositiveInteger(value) {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined;
}
export function sanitizeAllowedString(value, allowed) {
  return isString(value) && inList(allowed, value) ? value : undefined;
}
export function sanitizeBoundValue(value) {
  if (typeof value !== 'object' || value === null) return undefined;
  const obj = value;
  if ('$bindKey' in obj) {
    const bindKey = obj.$bindKey;
    return isString(bindKey) && bindKey.length > 0 ? {
      $bindKey: bindKey
    } : undefined;
  }
  if ('$bind' in obj) {
    const bind = obj.$bind;
    return isString(bind) && bind.length > 0 ? {
      $bind: bind
    } : undefined;
  }
  return undefined;
}
function sanitizeBindMapRef(raw) {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const {
    value,
    key
  } = raw;
  if (!isString(value) || value.length === 0) return undefined;
  if (!isString(key) || key.length === 0) return undefined;
  return {
    $bindMap: {
      value,
      key
    }
  };
}
export function sanitizeBindable(value, sanitizeLiteral) {
  if (typeof value === 'object' && value !== null) {
    const obj = value;
    if ('$bindMap' in obj) return sanitizeBindMapRef(obj.$bindMap);
    if ('$bind' in obj) {
      const bind = obj.$bind;
      return isString(bind) && bind.length > 0 ? {
        $bind: bind
      } : undefined;
    }
  }
  return sanitizeLiteral(value);
}
export function sanitizeFormatSpec(raw) {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const obj = raw;
  if (obj.type === 'duration') {
    const style = sanitizeAllowedString(obj.style, ALLOWED_DURATION_FORMAT_STYLES);
    if (!style) return undefined;
    return {
      type: 'duration',
      style
    };
  }
  if (obj.type !== 'date') return undefined;
  const style = sanitizeAllowedString(obj.style, ALLOWED_DATE_FORMAT_STYLES);
  if (!style) return undefined;
  const timezone = sanitizeBindable(obj.timezone, sanitizeString);
  return timezone !== undefined ? {
    type: 'date',
    style,
    timezone
  } : {
    type: 'date',
    style
  };
}

// Like sanitizeBindable, but preserves an optional $format on the ref. Only
// used for slots that render a string, so a formatter can never land where a
// non-string value is expected.
export function sanitizeFormattableBindable(value, sanitizeLiteral) {
  if (typeof value === 'object' && value !== null) {
    const obj = value;
    const format = sanitizeFormatSpec(obj.$format);
    if ('$bindMap' in obj) {
      const ref = sanitizeBindMapRef(obj.$bindMap);
      if (!ref) return undefined;
      return format ? Object.assign({}, ref, {
        $format: format
      }) : ref;
    }
    if ('$bind' in obj) {
      const bind = obj.$bind;
      if (!isString(bind) || bind.length === 0) return undefined;
      return format ? {
        $bind: bind,
        $format: format
      } : {
        $bind: bind
      };
    }
  }
  return sanitizeLiteral(value);
}
export function sanitizeRequiredChildren(raw, sanitizeElement) {
  if (!Array.isArray(raw)) return null;
  const children = raw.map(sanitizeElement).filter(c => c !== null);
  return children.length > 0 ? children : null;
}
export { isValidISODate } from '../../util/datetime';